import { useEffect } from 'react';

// Print utility hook for Alt+P shortcut
export const usePrintShortcut = (onPrint) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Alt + P
      if (e.altKey && (e.key === 'p' || e.key === 'P')) {
        e.preventDefault();
        onPrint();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onPrint]);
};

// Generate print content — plain black & white invoice/accounting style
// Optimised for low ink usage and printer-friendly compact A4 layout.
export const generatePrintContent = ({
  title,
  businessName = 'Shri Kripa Pharmacy',
  dateRange = '',
  headers,
  rows,
  summary = null,
  orientation = 'portrait',
}) => {
  const printStyles = `
    @page { size: A4 ${orientation}; margin: 10mm; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html, body {
      font-family: 'Times New Roman', Georgia, serif;
      font-size: 10px;
      line-height: 1.35;
      color: #000;
      background: #fff;
    }
    .print-container { width: 100%; max-width: 100%; }

    .print-header {
      text-align: center;
      padding-bottom: 6px;
      border-bottom: 1.5px solid #000;
      margin-bottom: 8px;
    }
    .business-name {
      font-size: 16px;
      font-weight: 700;
      letter-spacing: .5px;
      text-transform: uppercase;
      color: #000;
    }
    .module-title {
      font-size: 11px;
      font-weight: 600;
      color: #000;
      margin-top: 2px;
    }
    .date-range { font-size: 9px; color: #000; margin-top: 1px; }

    .print-summary {
      display: flex;
      justify-content: space-between;
      border: 1px solid #000;
      padding: 4px 6px;
      margin-bottom: 6px;
      font-size: 9px;
    }
    .summary-item { text-align: center; flex: 1; }
    .summary-item + .summary-item { border-left: 1px solid #000; }
    .summary-label {
      color: #000;
      font-size: 8px;
      text-transform: uppercase;
      letter-spacing: .3px;
    }
    .summary-value { font-weight: 700; font-size: 11px; color: #000; }
    /* All semantic colors collapse to black for B&W printing */
    .summary-value.positive,
    .summary-value.negative,
    .summary-value.neutral { color: #000; }

    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 9px;
      border: 1px solid #000;
    }
    th {
      background: #fff;
      color: #000;
      padding: 4px 5px;
      text-align: left;
      font-weight: 700;
      font-size: 9px;
      text-transform: uppercase;
      border: 1px solid #000;
      border-bottom: 1.5px solid #000;
    }
    th.text-right { text-align: right; }
    th.text-center { text-align: center; }
    td {
      padding: 3px 5px;
      border: 1px solid #000;
      vertical-align: top;
      color: #000;
    }
    td.text-right { text-align: right; }
    td.text-center { text-align: center; }
    /* No zebra striping - white only */
    tr:nth-child(even) td { background: #fff; }

    /* All amount/colour classes flatten to plain bold black */
    .amount-positive,
    .amount-negative,
    .amount-neutral { color: #000; font-weight: 700; }

    /* Badges → plain bordered text, no fills */
    .badge {
      display: inline-block;
      padding: 0 4px;
      border: 1px solid #000;
      border-radius: 0;
      font-size: 8px;
      font-weight: 600;
      color: #000;
      background: #fff;
    }
    .badge-blue,
    .badge-green,
    .badge-red,
    .badge-amber,
    .badge-purple {
      background: #fff;
      color: #000;
      border-color: #000;
    }

    .print-footer {
      margin-top: 8px;
      padding-top: 4px;
      border-top: 1px solid #000;
      font-size: 8px;
      color: #000;
      display: flex;
      justify-content: space-between;
    }
    .medicines-list { font-size: 8px; color: #000; }

    @media print {
      body { -webkit-print-color-adjust: economy; print-color-adjust: economy; }
      .no-print { display: none !important; }
    }
  `;

  const summaryHtml = summary ? `
    <div class="print-summary">
      ${summary.map(item => `
        <div class="summary-item">
          <div class="summary-label">${item.label}</div>
          <div class="summary-value ${item.type || 'neutral'}">${item.value}</div>
        </div>
      `).join('')}
    </div>
  ` : '';

  const tableHtml = `
    <table>
      <thead>
        <tr>
          ${headers.map(h => `<th class="${h.align === 'right' ? 'text-right' : h.align === 'center' ? 'text-center' : ''}">${h.label}</th>`).join('')}
        </tr>
      </thead>
      <tbody>
        ${rows.map(row => `
          <tr>
            ${row.map((cell, i) => `<td class="${headers[i]?.align === 'right' ? 'text-right' : headers[i]?.align === 'center' ? 'text-center' : ''}">${cell}</td>`).join('')}
          </tr>
        `).join('')}
      </tbody>
    </table>
  `;

  const now = new Date().toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${title} - ${businessName}</title>
  <style>${printStyles}</style>
</head>
<body>
  <div class="print-container">
    <div class="print-header">
      <div class="business-name">${businessName}</div>
      <div class="module-title">${title}</div>
      ${dateRange ? `<div class="date-range">${dateRange}</div>` : ''}
    </div>
    ${summaryHtml}
    ${tableHtml}
    <div class="print-footer">
      <span>Total: ${rows.length} entries</span>
      <span>Printed: ${now}</span>
    </div>
  </div>
  <script>
    window.onload = function () {
      setTimeout(function () { window.print(); }, 300);
    };
  </script>
</body>
</html>`;
};

// Open print window with generated content using Blob URL (no document.write).
export const openPrintWindow = (content) => {
  const blob = new Blob([content], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const printWindow = window.open(url, '_blank', 'width=900,height=700');
  if (printWindow) {
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }
};

// Format currency for print
export const formatPrintCurrency = (amount) => {
  return `₹${Math.abs(amount || 0).toLocaleString('en-IN')}`;
};

// Format date for print
export const formatPrintDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
  });
};
